"use client"

import { useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ArrowLeft, ChevronLeft, ChevronRight, Clock, Edit, LogOut, Package, PauseCircle, RotateCcw, ShoppingCart, Layers } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts"
import toast from "react-hot-toast"
import { API_ENDPOINTS } from "../../../constants/config"
import useInventoryUiConfig from "../../../hooks/useInventoryUiConfig"
import { buildIssueHistoryData, buildStockChartData, formatItemDisplayName } from "./inventoryDetailsUtils"
import BulkIssueDialog from "./BulkIssueDialog"
import BulkPurchaseDialog from "./BulkPurchaseDialog"

// Sentinel for "don't attribute this return to any party" — always offered
// alongside real parties so nobody's forced to pick someone this return isn't for.
const NO_PARTY_VALUE = "__no_party__"
const NO_PARTY_OPTION = (remaining) => ({
  customer_id: null,
  isNone: true,
  name: null,
  outstanding: remaining,
})

const InventoryItemDetails = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  const [item, setItem] = useState(null)
  // A single Return dialog, in one of two modes depending on whether the issue
  // it's opened from was linked to a real party (metadata.customer_id):
  //  - "simple": no party on record — return is tied to just this one transaction.
  //  - "party": party on record — shows everyone who's taken this item (deduped),
  //    defaulted to this row's own party but switchable, and returns against
  //    that party's total outstanding across all their issues of this item.
  const [returnTarget, setReturnTarget] = useState(null)
  const [returnQty, setReturnQty] = useState("")
  const [returnNotes, setReturnNotes] = useState("")
  const [returnSubmitting, setReturnSubmitting] = useState(false)
  const [parties, setParties] = useState([])
  const [partiesLoading, setPartiesLoading] = useState(false)
  const [selectedReturnParty, setSelectedReturnParty] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [vendors, setVendors] = useState([])
  const [categories, setCategories] = useState([])
  const [units, setUnits] = useState([])
  const [sizes, setSizes] = useState([])
  const [brands, setBrands] = useState([])
  const [colors, setColors] = useState([])
  const [colorEnabled, setColorEnabled] = useState(false)
  const [isSubCategoryEnabled, setIsSubCategoryEnabled] = useState(false)

  const [showEditForm, setShowEditForm] = useState(false)
  const [showAdjustForm, setShowAdjustForm] = useState(false)
  const [showUnholdForm, setShowUnholdForm] = useState(false)
  const [showHoldForm, setShowHoldForm] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const touchStartXRef = useRef(null)
  const { config: uiConfig, loading: uiConfigLoading } = useInventoryUiConfig()

  const [editItem, setEditItem] = useState({
    item_name: "",
    category: "",
    vendor_id: "",
    vendor_name: "",
    purchase_date: "",
    purchase_cost: 0,
    quantity_purchased: 0,
    quantity_available: 0,
    minimum_stock_level: 0,
    reorder_level: 0,
    unit_of_measurement: "",
    storage_location: "",
    description: "",
    brand: "",
    size: "",
    custom_attributes: {},
    is_active: true,
    status: "Available",
  })
  const [newAdjustment, setNewAdjustment] = useState({ new_quantity: 0, type: "", reason: "", notes: "" })
  const [unholdQuantity, setUnholdQuantity] = useState(0)
  const [holdQuantity, setHoldQuantity] = useState(0)
  const [showPurchaseForm, setShowPurchaseForm] = useState(false)
  const [showIssueForm, setShowIssueForm] = useState(false)
  // Synchronous re-entrancy guards — `actionLoading` state alone can't stop
  // a double-click, since the re-render that disables the button doesn't
  // land before a second click can fire.
  const purchaseSubmittingRef = useRef(false)
  const issueSubmittingRef = useRef(false)
  const [showBulkIssue, setShowBulkIssue] = useState(false)
  const [showBulkPurchase, setShowBulkPurchase] = useState(false)
  const [newPurchase, setNewPurchase] = useState({
    quantity: 0,
    cost_per_unit: 0,
    date: new Date().toISOString().split("T")[0],
    vendor_id: "placeholder",
    remarks: "",
  })
  const [newIssue, setNewIssue] = useState({ quantity: 0, issued_to: "", notes: "" })
  const canEdit = uiConfig?.permissions?.canEdit !== false
  const canHistory = uiConfig?.permissions?.canHistory !== false
  const showEditItem = uiConfig?.buttons?.showEditItem !== false
  const showAdjust = uiConfig?.buttons?.showAdjust !== false
  const showHold = uiConfig?.buttons?.showHold !== false
  const showUnhold = uiConfig?.buttons?.showUnhold !== false
  const showPurchase = uiConfig?.buttons?.showPurchase !== false
  const showIssue = uiConfig?.buttons?.showIssue !== false

  const openReturn = async (row) => {
    const remaining = Number((row.issuedQuantity - row.returnedQuantity).toFixed(10))
    setReturnNotes("")
    setSelectedReturnParty(null)
    setParties([])
    // Tentatively open in party mode while we check — every issue of this item
    // (not just this row) can surface a real party here. If none exist at all,
    // this downgrades to "simple" below.
    setReturnTarget({ ...row, mode: "party", remaining })
    setReturnQty("")
    setPartiesLoading(true)
    try {
      const token = localStorage.getItem("token")
      const response = await fetch(API_ENDPOINTS.inventoryItemParties(id), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) throw new Error("Failed to load parties")
      const data = await response.json()
      const list = data.data || []
      setParties(list)
      if (list.length === 0) {
        // This item has never had a real party linked to any issue — nothing to
        // dedupe, just return against this one transaction.
        setReturnTarget({ ...row, mode: "simple", remaining })
        setReturnQty(String(remaining))
        return
      }
      // Default to this row's own party if it's a real one. Otherwise default to
      // "no party" — this row was never attributed to anyone, so don't force a pick;
      // staff can still switch to a real party from the dropdown if that's actually
      // who this is for.
      const ownParty = row.customerId ? list.find((p) => p.customer_id === row.customerId) : null
      if (ownParty) {
        setSelectedReturnParty(ownParty)
        setReturnQty(String(ownParty.outstanding))
      } else {
        setSelectedReturnParty(NO_PARTY_OPTION(remaining))
        setReturnQty(String(remaining))
      }
    } catch {
      toast.error("Failed to load parties for this item")
      setReturnTarget({ ...row, mode: "simple", remaining })
      setReturnQty(String(remaining))
    } finally {
      setPartiesLoading(false)
    }
  }

  const closeReturn = () => {
    if (returnSubmitting) return
    setReturnTarget(null)
    setReturnQty("")
    setReturnNotes("")
    setParties([])
    setSelectedReturnParty(null)
  }

  const selectReturnPartyById = (customerId) => {
    if (customerId === NO_PARTY_VALUE) {
      const opt = NO_PARTY_OPTION(returnTarget?.remaining ?? 0)
      setSelectedReturnParty(opt)
      setReturnQty(String(opt.outstanding))
      return
    }
    const party = parties.find((p) => p.customer_id === customerId)
    if (!party) return
    setSelectedReturnParty(party)
    setReturnQty(String(party.outstanding))
  }

  const handleReturnSubmit = async (e) => {
    e.preventDefault()
    const qty = Number.parseFloat(returnQty)
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error("Enter a quantity greater than 0")
      return
    }

    const isPartyMode = returnTarget.mode === "party"
    if (isPartyMode && !selectedReturnParty) {
      toast.error("Select a party to return against")
      return
    }
    const usesRealParty = isPartyMode && !selectedReturnParty.isNone
    const cap = usesRealParty ? selectedReturnParty.outstanding : returnTarget.remaining
    if (qty > cap) {
      toast.error(usesRealParty ? `Only ${cap} outstanding with ${selectedReturnParty.name}` : `Only ${cap} left to return from this issue`)
      return
    }

    setReturnSubmitting(true)
    try {
      const token = localStorage.getItem("token")
      const url = usesRealParty
        ? API_ENDPOINTS.inventoryReturnToParty(id)
        : API_ENDPOINTS.inventoryTransactionReturn(returnTarget.transactionId)
      const body = usesRealParty
        ? { customer_id: selectedReturnParty.customer_id, quantity: qty, notes: returnNotes }
        : { quantity: qty, notes: returnNotes }
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) throw new Error(data?.message || "Failed to return stock")
      toast.success(usesRealParty ? `${qty} returned by ${selectedReturnParty.name} — stock restored` : `${qty} returned — stock restored`)
      closeReturn()
      fetchItem()
    } catch (err) {
      toast.error(err.message || "Failed to return stock")
    } finally {
      setReturnSubmitting(false)
    }
  }

  const fetchItem = async () => {
    if (!id) return
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const response = await fetch(API_ENDPOINTS.inventoryItem(id), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) throw new Error("Failed to fetch item details")
      const data = await response.json()
      setItem(data.data)
    } catch (err) {
      toast.error(err.message || "Failed to load details")
      setItem(null)
    } finally {
      setLoading(false)
    }
  }

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) return
      const response = await fetch(API_ENDPOINTS.vendors, { headers: { Authorization: `Bearer ${token}` } })
      if (!response.ok) return
      const data = await response.json()
      setVendors(data.data || [])
    } catch {}
  }

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) return
      const response = await fetch(API_ENDPOINTS.inventoryCategories, { headers: { Authorization: `Bearer ${token}` } })
      if (!response.ok) return
      const data = await response.json()
      setCategories(data.data || [])
    } catch {}
  }

  const fetchUnits = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) return
      const response = await fetch(API_ENDPOINTS.inventoryUnits, { headers: { Authorization: `Bearer ${token}` } })
      if (!response.ok) return
      const data = await response.json()
      setUnits(data.data || [])
    } catch {}
  }

  const fetchSizes = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) return
      const response = await fetch(API_ENDPOINTS.inventorySizes, { headers: { Authorization: `Bearer ${token}` } })
      if (!response.ok) return
      const data = await response.json()
      setSizes(data.data || [])
    } catch {}
  }

  const fetchBrands = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) return
      const response = await fetch(API_ENDPOINTS.inventoryBrands, { headers: { Authorization: `Bearer ${token}` } })
      if (!response.ok) return
      const data = await response.json()
      setBrands(data.data || [])
    } catch {}
  }

  const fetchColors = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) return
      const response = await fetch(API_ENDPOINTS.inventoryColors, { headers: { Authorization: `Bearer ${token}` } })
      if (!response.ok) return
      const data = await response.json()
      setColors(data.data || [])
    } catch {}
  }

  const fetchFeatureConfig = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) return
      const response = await fetch(API_ENDPOINTS.inventoryFeatureConfig, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) return
      const data = await response.json()
      setColorEnabled(Boolean(data?.data?.features?.color))
      setIsSubCategoryEnabled(Boolean(data?.data?.features?.sub_category))
    } catch {}
  }

  useEffect(() => {
    fetchItem()
  }, [id])

  useEffect(() => {
    if (canEdit || showPurchase || showIssue) {
      fetchVendors()
    }
    fetchCategories()
    fetchUnits()
    fetchSizes()
    fetchBrands()
    fetchColors()
    fetchFeatureConfig()
  }, [canEdit, showPurchase, showIssue])

  const imageCount = item?.images?.length || 0

  const showPrevImage = () => {
    if (imageCount === 0) return
    setLightboxIndex((prev) => (prev - 1 + imageCount) % imageCount)
  }

  const showNextImage = () => {
    if (imageCount === 0) return
    setLightboxIndex((prev) => (prev + 1) % imageCount)
  }

  useEffect(() => {
    if (lightboxIndex === null) return
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") showPrevImage()
      else if (e.key === "ArrowRight") showNextImage()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [lightboxIndex, imageCount])

  const handleLightboxTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX
  }

  const handleLightboxTouchEnd = (e) => {
    if (touchStartXRef.current === null) return
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current
    const SWIPE_THRESHOLD = 50
    if (deltaX > SWIPE_THRESHOLD) showPrevImage()
    else if (deltaX < -SWIPE_THRESHOLD) showNextImage()
    touchStartXRef.current = null
  }

  const openEditForm = () => {
    if (!item) return
    setEditItem({
      ...item,
      custom_attributes: item.custom_attributes || {},
      purchase_date: item.purchase_date ? new Date(item.purchase_date).toISOString().split("T")[0] : "",
      is_active: item.is_active !== false,
    })
    setShowEditForm(true)
  }

  const handleEditItem = async () => {
    if (!item) return
    if (!editItem.item_name || !editItem.category) {
      toast.error("Please fill all required fields")
      return
    }
    if (!editItem.vendor_id && !editItem.brand) {
      toast.error("Please select either a vendor or a brand")
      return
    }

    const payload = { ...editItem }
    if (!payload.custom_attributes) payload.custom_attributes = {}
    delete payload.quantity_available
    delete payload.quantity_purchased
    delete payload.on_hold_quantity
    if (!payload.vendor_id || payload.vendor_id === "placeholder") {
      delete payload.vendor_id
      delete payload.vendor_name
    }

    try {
      setActionLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const response = await fetch(API_ENDPOINTS.inventoryItem(item.inventory_id), {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      })
      if (!response.ok) throw new Error(`Failed to update inventory item: ${response.statusText}`)
      const data = await response.json()
      setItem(data.data)
      setShowEditForm(false)
      toast.success("Item updated successfully.")
      fetchItem()
    } catch (err) {
      toast.error(err.message || "Failed to update item")
    } finally {
      setActionLoading(false)
    }
  }

  const handleAdjustStock = async () => {
    if (!item) return
    const newQty = Number(newAdjustment.new_quantity)
    if (!Number.isFinite(newQty) || newQty < 0) {
      toast.error("New quantity must be a non-negative number")
      return
    }
    try {
      setActionLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const adjustEndpoint =
        typeof API_ENDPOINTS.inventoryAdjust === "function"
          ? API_ENDPOINTS.inventoryAdjust(item.inventory_id)
          : `${API_ENDPOINTS.inventory}/${item.inventory_id}/adjust`
      const response = await fetch(adjustEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newAdjustment),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.message || "Failed to adjust stock")
      setItem(data.data)
      setShowAdjustForm(false)
      setNewAdjustment({ new_quantity: 0, type: "", reason: "", notes: "" })
      toast.success("Stock adjusted successfully.")
      fetchItem()
    } catch (err) {
      toast.error(err.message || "Failed to adjust stock")
    } finally {
      setActionLoading(false)
    }
  }

  const handleUnholdStock = async () => {
    if (!item) return
    const qty = Number(unholdQuantity) || 0
    if (qty <= 0) {
      toast.error("Quantity must be greater than 0")
      return
    }
    if (qty > (Number(item.on_hold_quantity) || 0)) {
      toast.error("Quantity cannot exceed on-hold stock")
      return
    }
    try {
      setActionLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const unholdEndpoint =
        typeof API_ENDPOINTS.inventoryUnhold === "function"
          ? API_ENDPOINTS.inventoryUnhold(item.inventory_id)
          : `${API_ENDPOINTS.inventory}/${item.inventory_id}/unhold`
      const response = await fetch(unholdEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ quantity: qty }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.message || "Failed to release hold stock")
      setItem(data.data)
      setShowUnholdForm(false)
      setUnholdQuantity(0)
      toast.success("Hold stock released successfully.")
      fetchItem()
    } catch (err) {
      toast.error(err.message || "Failed to release hold stock")
    } finally {
      setActionLoading(false)
    }
  }

  const handleHoldStock = async () => {
    if (!item) return
    const qty = Number.parseFloat(holdQuantity) || 0
    if (qty <= 0) {
      toast.error("Quantity must be greater than 0")
      return
    }
    try {
      setActionLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const response = await fetch(API_ENDPOINTS.inventoryHold(item.inventory_id), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ quantity: qty }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.message || "Failed to place stock on hold")
      setItem(data.data)
      setShowHoldForm(false)
      setHoldQuantity(0)
      toast.success("Stock placed on hold")
      fetchItem()
    } catch (err) {
      toast.error(err.message || "Failed to place stock on hold")
    } finally {
      setActionLoading(false)
    }
  }

  const handlePurchaseStock = async () => {
    if (!item || purchaseSubmittingRef.current) return
    const qty = Number(newPurchase.quantity)
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error("Quantity must be greater than 0")
      return
    }
    const costPerUnit = Number(newPurchase.cost_per_unit)
    if (!Number.isFinite(costPerUnit) || costPerUnit < 0) {
      toast.error("Cost per unit must be a non-negative number")
      return
    }
    purchaseSubmittingRef.current = true
    try {
      setActionLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const payload = {
        quantity: qty,
        cost_per_unit: costPerUnit,
        date: newPurchase.date,
        remarks: newPurchase.remarks,
      }
      if (newPurchase.vendor_id && newPurchase.vendor_id !== "placeholder") {
        const selectedVendor = vendors.find((v) => v._id === newPurchase.vendor_id)
        payload.vendor_id = newPurchase.vendor_id
        payload.vendor_name = selectedVendor ? selectedVendor.name : ""
      }
      const response = await fetch(API_ENDPOINTS.inventoryPurchase(item.inventory_id), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.message || "Failed to add purchase")
      setItem(data.data)
      setShowPurchaseForm(false)
      setNewPurchase({
        quantity: 0,
        cost_per_unit: 0,
        date: new Date().toISOString().split("T")[0],
        vendor_id: "placeholder",
        remarks: "",
      })
      toast.success("Purchase recorded successfully.")
      fetchItem()
    } catch (err) {
      toast.error(err.message || "Failed to add purchase")
    } finally {
      setActionLoading(false)
      purchaseSubmittingRef.current = false
    }
  }

  const handleIssueStock = async () => {
    if (!item || issueSubmittingRef.current) return
    const qty = Number(newIssue.quantity)
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error("Quantity must be greater than 0")
      return
    }
    if (qty > (Number(item.quantity_available) || 0)) {
      toast.error("Quantity cannot exceed available stock")
      return
    }
    issueSubmittingRef.current = true
    try {
      setActionLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const response = await fetch(API_ENDPOINTS.inventoryIssue(item.inventory_id), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          quantity: qty,
          issued_to: newIssue.issued_to,
          notes: newIssue.notes,
        }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.message || "Failed to issue stock")
      setItem(data.data)
      setShowIssueForm(false)
      setNewIssue({ quantity: 0, issued_to: "", notes: "" })
      toast.success("Stock issued successfully.")
      fetchItem()
    } catch (err) {
      toast.error(err.message || "Failed to issue stock")
    } finally {
      setActionLoading(false)
      issueSubmittingRef.current = false
    }
  }

  if (loading || uiConfigLoading) {
    return (
      <div className="p-6 flex justify-center items-center h-[60vh]">
        <div className="flex items-center space-x-2">
          <Clock className="animate-spin h-5 w-5 text-[#0263cc]" />
          <span className="text-gray-600">Loading item details...</span>
        </div>
      </div>
    )
  }

  if (!item) {
    return (
      <div className="w-full mx-auto">
        <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
          <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10">
            <CardTitle className="flex items-center gap-2 text-base sm:text-xl">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-full border-[#0263cc]/30 text-[#0263cc] hover:bg-[#0263cc]/5"
                onClick={() => navigate("/dashboard/inventory")}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <span>Item Details</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 text-sm text-gray-500">Item not found.</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="w-full mx-auto space-y-4 pb-24 sm:pb-8">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10">
          <CardTitle className="flex items-center gap-2 text-base sm:text-xl">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full border-[#0263cc]/30 text-[#0263cc] hover:bg-[#0263cc]/5"
              onClick={() => navigate("/dashboard/inventory")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span>Item Details</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-6">
          {item.images?.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {item.images.map((url, index) => (
                <button
                  key={url}
                  type="button"
                  onClick={() => setLightboxIndex(index)}
                  className="h-32 w-32 rounded-lg border border-gray-200 overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#0263cc]"
                >
                  <img
                    src={url}
                    alt={`${item.item_name} ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {canEdit && showEditItem && (
              <Button
                variant="outline"
                className="w-full sm:w-auto text-[#0263cc] border-[#0263cc]/30 hover:bg-[#0263cc]/5"
                onClick={() => navigate(`/dashboard/inventory/edit/${id}`, { state: { item } })}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
            {canEdit && showPurchase && (
              <Button
                variant="outline"
                className="w-full sm:w-auto text-green-700 border-green-300 hover:bg-green-50"
                onClick={() => {
                  setNewPurchase({
                    quantity: 0,
                    cost_per_unit: 0,
                    date: new Date().toISOString().split("T")[0],
                    vendor_id: "placeholder",
                    remarks: "",
                  })
                  setShowPurchaseForm(true)
                }}
              >
                <Package className="h-4 w-4 mr-2" />
                Purchase
              </Button>
            )}
            {canEdit && showIssue && (
              <Button
                variant="outline"
                className="w-full sm:w-auto text-orange-700 border-orange-300 hover:bg-orange-50"
                onClick={() => {
                  setNewIssue({ quantity: 0, issued_to: "", notes: "" })
                  setShowIssueForm(true)
                }}
                disabled={!item?.quantity_available || item.quantity_available <= 0}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Issue
              </Button>
            )}
            {canEdit && showIssue && (
              <Button
                variant="outline"
                className="w-full sm:w-auto text-purple-700 border-purple-300 hover:bg-purple-50"
                onClick={() => setShowBulkIssue(true)}
              >
                <Layers className="h-4 w-4 mr-2" />
                Bulk Issue
              </Button>
            )}
            {canEdit && showPurchase && (
              <Button
                variant="outline"
                className="w-full sm:w-auto text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                onClick={() => setShowBulkPurchase(true)}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Bulk Purchase
              </Button>
            )}
            {canEdit && showAdjust && (
              <Button
                variant="outline"
                className="w-full sm:w-auto text-purple-700 border-purple-300 hover:bg-purple-50"
                onClick={() => {
                  setNewAdjustment({
                    new_quantity: item.quantity_available || 0,
                    type: "",
                    reason: "",
                    notes: "",
                  })
                  setShowAdjustForm(true)
                }}
              >
                Adjust Stock
              </Button>
            )}
            {canEdit && showUnhold && (
              <Button
                variant="outline"
                className="w-full sm:w-auto text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                onClick={() => {
                  setUnholdQuantity(0)
                  setShowUnholdForm(true)
                }}
                disabled={!item?.on_hold_quantity || item.on_hold_quantity <= 0}
              >
                Unhold
              </Button>
            )}
            {canEdit && showHold && (
              <Button
                variant="outline"
                className="w-full sm:w-auto text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={() => {
                  setHoldQuantity(0)
                  setShowHoldForm(true)
                }}
                disabled={!item?.quantity_available || item.quantity_available <= 0}
              >
                <PauseCircle className="h-4 w-4 mr-2" />
                Hold
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-500">Item</span>
              <p className="font-medium">{formatItemDisplayName(item)}</p>
            </div>
            <div>
              <span className="text-gray-500">Category</span>
              <p className="font-medium">{item.category}</p>
            </div>
            {isSubCategoryEnabled && item.sub_category && (
              <div>
                <span className="text-gray-500">Sub-category</span>
                <p className="font-medium">{item.sub_category}</p>
              </div>
            )}
            {item.brand && (
              <div>
                <span className="text-gray-500">Brand</span>
                <p className="font-medium">{item.brand}</p>
              </div>
            )}
            {item.size && (
              <div>
                <span className="text-gray-500">Size</span>
                <p className="font-medium">{item.size}</p>
              </div>
            )}
            {colorEnabled && item.custom_attributes?.color && (
              <div>
                <span className="text-gray-500">Color</span>
                <p className="font-medium">{item.custom_attributes.color}</p>
              </div>
            )}
            <div>
              <span className="text-gray-500">Available</span>
              <p className="font-medium">
                {item.quantity_available} {item.unit_of_measurement || ""}
              </p>
            </div>
            <div>
              <span className="text-gray-500">Status</span>
              <p className="font-medium">{item.status}</p>
            </div>
            <div>
              <span className="text-gray-500">Min / Reorder level</span>
              <p className="font-medium">
                {item.minimum_stock_level} / {item.reorder_level}
              </p>
            </div>
            <div>
              <span className="text-gray-500">Total purchased</span>
              <p className="font-medium">
                {item.total_purchased ?? 0} {item.unit_of_measurement || ""}
              </p>
            </div>
            <div>
              <span className="text-gray-500">Total issued</span>
              <p className="font-medium">
                {item.total_issued ?? 0} {item.unit_of_measurement || ""}
              </p>
            </div>
            {item.description && (
              <div className="sm:col-span-2">
                <span className="text-gray-500">Description</span>
                <p className="font-medium">{item.description}</p>
              </div>
            )}
            {item.storage_location && (
              <div className="sm:col-span-2">
                <span className="text-gray-500">Storage</span>
                <p className="font-medium">{item.storage_location}</p>
              </div>
            )}
          </div>

          {canHistory && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Stock count</h4>
            <div className="h-56 sm:h-64 w-full">
              {buildStockChartData(item).length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={buildStockChartData(item)} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="stock" stroke="#0263cc" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">No history yet</div>
              )}
            </div>
          </div>
          )}

          {canHistory && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Issue history</h4>
            {buildIssueHistoryData(item).length > 0 ? (
              <div className="border rounded-md overflow-hidden">
                <div className="overflow-x-auto pb-2">
                <table className="w-full min-w-[680px] text-xs sm:text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Date</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Type</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Qty</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Party</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Notes</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Return</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {buildIssueHistoryData(item).map((row, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                          {new Date(row.date).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                              row.direction === "+" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
                            }`}
                          >
                            {row.type}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-medium whitespace-nowrap">
                          <span className={row.direction === "+" ? "text-green-700" : "text-orange-700"}>
                            {row.direction}
                            {row.quantity} {item.unit_of_measurement || ""}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{row.party || "Walk-in customer"}</td>
                        <td className="px-3 py-2 text-gray-600">{row.notes || "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {row.rawType === "issue" && row.transactionId ? (
                            row.issuedQuantity - row.returnedQuantity > 0 ? (
                              <button
                                type="button"
                                onClick={() => openReturn(row)}
                                className="inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-2 py-1 text-xs font-medium text-violet-700 hover:bg-violet-100"
                              >
                                <RotateCcw className="h-3 w-3" />
                                Return
                                {row.returnedQuantity > 0 && (
                                  <span className="text-violet-500">
                                    ({Number((row.issuedQuantity - row.returnedQuantity).toFixed(10))} left)
                                  </span>
                                )}
                              </button>
                            ) : (
                              <span className="text-xs text-violet-700">Returned</span>
                            )
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No issue history recorded</p>
            )}
          </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={canEdit && showEditForm && showEditItem} onOpenChange={setShowEditForm}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Edit Inventory Item</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-item-name">Item Name*</Label>
              <Input
                id="edit-item-name"
                value={editItem.item_name}
                onChange={(e) => setEditItem({ ...editItem, item_name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-category">Category*</Label>
              <Select value={editItem.category} onValueChange={(value) => setEditItem({ ...editItem, category: value })}>
                <SelectTrigger id="edit-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category._id} value={category.name}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-vendor">Vendor</Label>
              <Select
                value={editItem.vendor_id}
                onValueChange={(value) => {
                  const selectedVendor = vendors.find((v) => v._id === value)
                  setEditItem({
                    ...editItem,
                    vendor_id: value,
                    vendor_name: selectedVendor ? selectedVendor.name : "",
                  })
                }}
              >
                <SelectTrigger id="edit-vendor">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder">Select vendor</SelectItem>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor._id} value={vendor._id}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-unit">Unit of Measurement</Label>
              <Select value={editItem.unit_of_measurement} onValueChange={(value) => setEditItem({ ...editItem, unit_of_measurement: value })}>
                <SelectTrigger id="edit-unit">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit._id} value={unit.name}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-min-stock">Minimum Stock Level</Label>
              <Input
                id="edit-min-stock"
                type="number"
                step="any"
                value={editItem.minimum_stock_level}
                onChange={(e) => setEditItem({ ...editItem, minimum_stock_level: Number.parseFloat(e.target.value) || 0 })}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div>
              <Label htmlFor="edit-reorder-level">Reorder Level</Label>
              <Input
                id="edit-reorder-level"
                type="number"
                step="any"
                value={editItem.reorder_level}
                onChange={(e) => setEditItem({ ...editItem, reorder_level: Number.parseFloat(e.target.value) || 0 })}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div>
              <Label htmlFor="edit-storage-location">Storage Location</Label>
              <Input
                id="edit-storage-location"
                value={editItem.storage_location}
                onChange={(e) => setEditItem({ ...editItem, storage_location: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-purchase-cost">Purchase Cost (Rs)</Label>
              <Input
                id="edit-purchase-cost"
                type="number"
                value={editItem.purchase_cost}
                onChange={(e) => setEditItem({ ...editItem, purchase_cost: Number.parseFloat(e.target.value) || 0 })}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div>
              <Label htmlFor="edit-purchase-date">Purchase Date</Label>
              <Input
                id="edit-purchase-date"
                type="date"
                value={editItem.purchase_date}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setEditItem({ ...editItem, purchase_date: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-brand">Brand</Label>
              <Input
                id="edit-brand"
                value={editItem.brand}
                onChange={(e) => setEditItem({ ...editItem, brand: e.target.value })}
                placeholder="Type brand name"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Tip: Type the first letter to see matching brands</p>
              {editItem.brand &&
                brands.filter((brand) => brand.name.toLowerCase().startsWith(editItem.brand.toLowerCase())).length > 0 && (
                  <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-sm text-sm">
                    {brands
                      .filter((brand) => brand.name.toLowerCase().startsWith(editItem.brand.toLowerCase()))
                      .slice(0, 8)
                      .map((brand) => (
                        <button
                          key={brand._id}
                          type="button"
                          className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
                          onClick={() => setEditItem({ ...editItem, brand: brand.name })}
                        >
                          {brand.name}
                        </button>
                      ))}
                  </div>
                )}
            </div>
            <div>
              <Label htmlFor="edit-size">Size</Label>
              <Select value={editItem.size} onValueChange={(value) => setEditItem({ ...editItem, size: value })}>
                <SelectTrigger id="edit-size">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  {sizes.map((size) => (
                    <SelectItem key={size._id} value={size.name}>
                      {size.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {colorEnabled && (
              <div>
                <Label htmlFor="edit-color">Color</Label>
                <Select
                  value={editItem.custom_attributes?.color || ""}
                  onValueChange={(value) =>
                    setEditItem({
                      ...editItem,
                      custom_attributes: {
                        ...(editItem.custom_attributes || {}),
                        color: value,
                      },
                    })
                  }
                >
                  <SelectTrigger id="edit-color">
                    <SelectValue placeholder="Select color" />
                  </SelectTrigger>
                  <SelectContent>
                    {colors.map((color) => (
                      <SelectItem key={color._id} value={color.name}>
                        {color.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="sm:col-span-2">
              <Label htmlFor="edit-description">Description</Label>
              <Input
                id="edit-description"
                value={editItem.description}
                onChange={(e) => setEditItem({ ...editItem, description: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-item-active-status">Active Status</Label>
              <Select
                value={editItem.is_active ? "active" : "inactive"}
                onValueChange={(value) => setEditItem({ ...editItem, is_active: value === "active" })}
              >
                <SelectTrigger id="edit-item-active-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditForm(false)}>
              Cancel
            </Button>
            <Button className="bg-[#0263cc] hover:bg-[#0263cc]/90" onClick={handleEditItem} disabled={actionLoading}>
              {actionLoading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
              Update Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={canEdit && showPurchaseForm && showPurchase} onOpenChange={setShowPurchaseForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Purchase Stock</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="purchase-quantity">Quantity*</Label>
              <Input
                id="purchase-quantity"
                type="number"
                min={1}
                value={newPurchase.quantity}
                onChange={(e) => setNewPurchase({ ...newPurchase, quantity: Number(e.target.value) || 0 })}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div>
              <Label htmlFor="purchase-cost">Cost per Unit (Rs)</Label>
              <Input
                id="purchase-cost"
                type="number"
                min={0}
                value={newPurchase.cost_per_unit}
                onChange={(e) => setNewPurchase({ ...newPurchase, cost_per_unit: Number(e.target.value) || 0 })}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div>
              <Label htmlFor="purchase-date">Purchase Date</Label>
              <Input
                id="purchase-date"
                type="date"
                value={newPurchase.date}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => setNewPurchase({ ...newPurchase, date: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="purchase-vendor">Vendor</Label>
              <Select value={newPurchase.vendor_id} onValueChange={(value) => setNewPurchase({ ...newPurchase, vendor_id: value })}>
                <SelectTrigger id="purchase-vendor">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder">No vendor</SelectItem>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor._id} value={vendor._id}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="purchase-remarks">Remarks</Label>
              <Input
                id="purchase-remarks"
                value={newPurchase.remarks}
                onChange={(e) => setNewPurchase({ ...newPurchase, remarks: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchaseForm(false)}>
              Cancel
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handlePurchaseStock} disabled={actionLoading}>
              {actionLoading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
              Add Purchase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={canEdit && showIssueForm && showIssue} onOpenChange={setShowIssueForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Issue Stock</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div>
              <Label>Available Quantity</Label>
              <Input value={item.quantity_available ?? 0} readOnly className="bg-gray-50" />
            </div>
            <div>
              <Label htmlFor="issue-quantity">Quantity to issue*</Label>
              <Input
                id="issue-quantity"
                type="number"
                min={1}
                max={item.quantity_available ?? undefined}
                value={newIssue.quantity}
                onChange={(e) => setNewIssue({ ...newIssue, quantity: Number(e.target.value) || 0 })}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div>
              <Label htmlFor="issue-issued-to">Issued To</Label>
              <Input
                id="issue-issued-to"
                value={newIssue.issued_to}
                onChange={(e) => setNewIssue({ ...newIssue, issued_to: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="issue-notes">Notes</Label>
              <Input
                id="issue-notes"
                value={newIssue.notes}
                onChange={(e) => setNewIssue({ ...newIssue, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIssueForm(false)}>
              Cancel
            </Button>
            <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={handleIssueStock} disabled={actionLoading}>
              {actionLoading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
              Issue Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={canEdit && showAdjustForm && showAdjust} onOpenChange={setShowAdjustForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Adjust Stock</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Current Quantity</Label>
              <Input value={item.quantity_available ?? 0} readOnly className="bg-gray-50" />
            </div>
            <div>
              <Label htmlFor="adjust-type">Type</Label>
              <Select
                value={newAdjustment.type || "none"}
                onValueChange={(value) => setNewAdjustment({ ...newAdjustment, type: value === "none" ? "" : value })}
              >
                <SelectTrigger id="adjust-type">
                  <SelectValue placeholder="Select type (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="Error">Error</SelectItem>
                  <SelectItem value="Return">Return</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="adjust-new-quantity">New Quantity*</Label>
              <Input
                id="adjust-new-quantity"
                type="number"
                min={0}
                value={newAdjustment.new_quantity}
                onChange={(e) => setNewAdjustment({ ...newAdjustment, new_quantity: Number(e.target.value) || 0 })}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div>
              <Label htmlFor="adjust-reason">Reason</Label>
              <Input
                id="adjust-reason"
                value={newAdjustment.reason}
                onChange={(e) => setNewAdjustment({ ...newAdjustment, reason: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="adjust-notes">Notes</Label>
              <Input
                id="adjust-notes"
                value={newAdjustment.notes}
                onChange={(e) => setNewAdjustment({ ...newAdjustment, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustForm(false)}>
              Cancel
            </Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleAdjustStock} disabled={actionLoading}>
              {actionLoading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
              Apply Adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={canEdit && showUnholdForm && showUnhold} onOpenChange={setShowUnholdForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Release On-Hold Stock</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div>
              <Label>Current On-Hold Quantity</Label>
              <Input value={item.on_hold_quantity ?? 0} readOnly className="bg-gray-50" />
            </div>
            <div>
              <Label htmlFor="unhold-quantity">Quantity to release*</Label>
              <Input
                id="unhold-quantity"
                type="number"
                min={1}
                value={unholdQuantity}
                onChange={(e) => setUnholdQuantity(Number(e.target.value) || 0)}
                onFocus={(e) => e.target.select()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnholdForm(false)}>
              Cancel
            </Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleUnholdStock} disabled={actionLoading}>
              {actionLoading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
              Release Hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={canEdit && showHoldForm && showHold} onOpenChange={setShowHoldForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Hold Stock</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div>
              <Label>Available Quantity</Label>
              <Input value={item.quantity_available ?? 0} readOnly className="bg-gray-50" />
            </div>
            <div>
              <Label htmlFor="hold-quantity">Quantity to hold*</Label>
              <Input
                id="hold-quantity"
                type="number"
                min={1}
                value={holdQuantity}
                onChange={(e) => setHoldQuantity(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHoldForm(false)}>
              Cancel
            </Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleHoldStock} disabled={actionLoading}>
              {actionLoading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
              Place On Hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={lightboxIndex !== null} onOpenChange={(open) => !open && setLightboxIndex(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{item.item_name}</DialogTitle>
          </DialogHeader>
          {lightboxIndex !== null && item.images?.[lightboxIndex] && (
            <div className="space-y-2">
              <div
                className="relative"
                onTouchStart={handleLightboxTouchStart}
                onTouchEnd={handleLightboxTouchEnd}
              >
                {imageCount > 1 && (
                  <button
                    type="button"
                    onClick={showPrevImage}
                    aria-label="Previous image"
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-9 w-9 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 focus:outline-none focus:ring-2 focus:ring-white"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                )}
                <img
                  src={item.images[lightboxIndex]}
                  alt={`${item.item_name} ${lightboxIndex + 1}`}
                  className="w-full max-h-[70vh] object-contain rounded-lg"
                />
                {imageCount > 1 && (
                  <button
                    type="button"
                    onClick={showNextImage}
                    aria-label="Next image"
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-9 w-9 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 focus:outline-none focus:ring-2 focus:ring-white"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                )}
              </div>
              {imageCount > 1 && (
                <div className="text-center text-xs text-gray-500">
                  {lightboxIndex + 1} / {imageCount}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BulkIssueDialog
        open={showBulkIssue}
        onOpenChange={setShowBulkIssue}
        preloadItem={item}
        onSuccess={fetchItem}
      />
      <BulkPurchaseDialog
        open={showBulkPurchase}
        onOpenChange={setShowBulkPurchase}
        preloadItem={item}
        onSuccess={fetchItem}
      />

      <Dialog open={Boolean(returnTarget)} onOpenChange={(open) => !open && closeReturn()}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-100">
                <RotateCcw className="h-4 w-4 text-violet-700" />
              </span>
              Return stock
            </DialogTitle>
          </DialogHeader>
          {returnTarget && (
            <form onSubmit={handleReturnSubmit} className="space-y-4">
              {returnTarget.mode === "simple" ? (
                <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                  Issued {returnTarget.issuedQuantity}
                  {returnTarget.party && returnTarget.party !== "—" ? ` to ${returnTarget.party}` : ""}
                  {returnTarget.returnedQuantity > 0 && ` · ${returnTarget.returnedQuantity} already returned`}
                </p>
              ) : (
                <>
                  <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                    Opened from the row: {returnTarget.issuedQuantity} issued to {returnTarget.party}
                    {returnTarget.date ? ` on ${new Date(returnTarget.date).toLocaleDateString()}` : ""}
                  </p>

                  <div>
                    <Label className="text-xs">Returned by</Label>
                    {partiesLoading ? (
                      <p className="text-sm text-gray-400 py-2">Loading parties...</p>
                    ) : parties.length === 0 ? (
                      <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 mt-1.5">
                        Nobody has an outstanding issue of this item to return — every unit issued has already been
                        returned, or none has been issued yet.
                      </p>
                    ) : (
                      <Select
                        value={selectedReturnParty ? selectedReturnParty.customer_id || NO_PARTY_VALUE : ""}
                        onValueChange={selectReturnPartyById}
                      >
                        <SelectTrigger className="mt-1.5">
                          <SelectValue placeholder="Select who's returning it…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_PARTY_VALUE}>No party — just this transaction</SelectItem>
                          {parties.map((p) => (
                            <SelectItem key={p.customer_id} value={p.customer_id}>
                              {p.name} ({p.outstanding} issued)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </>
              )}

              {(returnTarget.mode === "simple" || selectedReturnParty) && (
                <>
                  <div>
                    <Label className="text-xs">
                      Quantity to return{" "}
                      {returnTarget.mode === "simple"
                        ? `(max ${returnTarget.remaining})`
                        : `(max ${selectedReturnParty.outstanding})`}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      max={returnTarget.mode === "simple" ? returnTarget.remaining : selectedReturnParty.outstanding}
                      value={returnQty}
                      onChange={(e) => setReturnQty(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Notes (optional)</Label>
                    <Input
                      value={returnNotes}
                      onChange={(e) => setReturnNotes(e.target.value)}
                      placeholder="Why is it coming back?"
                    />
                  </div>

                  {returnTarget.mode === "party" && !selectedReturnParty.isNone && (
                    <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      <span>⚠️</span>
                      <span>
                        This will also post a return entry to <strong>{selectedReturnParty.name}</strong>&apos;s
                        customer ledger (amount not tracked yet).
                      </span>
                    </p>
                  )}
                </>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeReturn} disabled={returnSubmitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-violet-600 hover:bg-violet-700"
                  disabled={returnSubmitting || (returnTarget.mode === "party" && !selectedReturnParty)}
                >
                  {returnSubmitting ? "Returning..." : "Confirm return"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default InventoryItemDetails
